import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { FiFileText, FiUpload, FiTrash2, FiLoader } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CommunicationsAnnouncementResult } from '../../../model/CommunicationsAnnouncementsResult';
import api, { combinedTicker } from '../../../api/api';
import { toast } from '../Toast';
import DeleteAnnouncementModal from './DeleteAnnouncementModal';

interface Props {
    detail: CommunicationsAnnouncementResult;
    onSaved: () => void;
    onCancel: () => void;
    onDeleted: () => void;
}

const TZ = 'Australia/Sydney';
const MAX_BYTES = 25 * 1024 * 1024;
const splitTags = (s: string) => s.split(/[,·]/).map(x => x.trim()).filter(x => x);

// spec 2:58009. Category is read-only (worked out from the headline and tags, as in the list); no PDF file size (not stored yet).
const AnnouncementEditForm: React.FC<Props> = ({ detail, onSaved, onCancel, onDeleted }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const { row } = detail;
    const sydney = moment(row.date).tz(TZ);
    const [headline, setHeadline] = useState(row.headline);
    const [date, setDate] = useState(sydney.format('YYYY-MM-DD'));
    const [time, setTime] = useState(sydney.format('HH:mm'));
    const [priceSensitive, setPriceSensitive] = useState(row.priceSensitive);
    const [tags, setTags] = useState(row.tags.join(', '));
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const ticker = combinedTicker(stock.ticker, stock.exchange);

    const pick = (f: File | undefined) => {
        if (!f) return;
        if (!/\.pdf$/i.test(f.name)) { toast.error('Only a PDF can be uploaded'); return; }
        if (f.size > MAX_BYTES) { toast.error('The PDF must be 25 MB or smaller'); return; }
        setFile(f);
    };

    const save = async () => {
        const when = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', TZ);
        if (!headline.trim()) { toast.error('Headline is required'); return; }
        if (!when.isValid()) { toast.error('Date and time are required'); return; }
        // untouched date keeps its seconds, so the record still matches the ASX copy
        const dateChanged = date !== sydney.format('YYYY-MM-DD') || time !== sydney.format('HH:mm');
        const recordChanged = headline.trim() !== row.headline || dateChanged || priceSensitive !== row.priceSensitive || !!file;
        const newTags = splitTags(tags);
        const tagsChanged = newTags.join('|') !== row.tags.join('|');
        if (!recordChanged && !tagsChanged) { onCancel(); return; }

        setSaving(true);
        if (recordChanged) {
            const announcement = {
                headline: headline.trim(), date: dateChanged ? when.toISOString() : row.date, priceSensitive, remotePath: '', localPath: '', sendHistory: [],
                videoUrl: detail.videoUrl || '', videoDescription: detail.videoDescription || '', thumbnailPageNum: detail.thumbnailPageNum || 1,
            };
            const existing = { headline: row.headline, date: row.date } as typeof announcement;
            const result = await api().saveAnnouncement(announcement, existing, file, ticker).catch(e => ({ success: false, errorMessage: e.message }));
            if (!result.success) { setSaving(false); toast.error(`Save failed: ${result.errorMessage}`); return; }
        }
        if (tagsChanged) {
            const result = await api().updateAnnouncementTags(ticker, row.sendGuid!, newTags).catch(e => ({ success: false, errorMessage: e.message }));
            if (!result.success) { setSaving(false); toast.error(`Saving tags failed: ${result.errorMessage}`); return; }
        }
        setSaving(false);
        toast.success('Changes saved');
        onSaved();
    };

    const fetched = moment(detail.fetched);
    const pdfName = (row.localPath || '').split('/').pop() || 'announcement.pdf';

    return <div className="announcement-edit">
        <div className="card">
            <h2>Edit the Record</h2>
            <p className="sub">Changing this changes what shows on your website. It does not change what was lodged with the ASX.</p>

            <label className="field">
                <span className="label">Headline <em>required</em></span>
                <input value={headline} onChange={e => setHeadline(e.target.value)} />
            </label>

            <div className="three">
                <label className="field">
                    <span className="label">Date <em>Sydney</em></span>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} />
                </label>
                <label className="field">
                    <span className="label">Time <em>Sydney (AEST/AEDT)</em></span>
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} />
                </label>
                <label className="field">
                    <span className="label">Price sensitive</span>
                    <select value={priceSensitive ? 'Yes' : 'No'} onChange={e => setPriceSensitive(e.target.value === 'Yes')}>
                        <option>Yes</option>
                        <option>No</option>
                    </select>
                </label>
            </div>

            <label className="field">
                <span className="label">Category <em>worked out from the headline and tags</em></span>
                <input value={row.category} readOnly disabled />
            </label>

            <label className="field">
                <span className="label">Tags <em>separate with commas</em></span>
                <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. Drilling, Gold Mountain" />
            </label>
        </div>

        <div className="card">
            <h2>The Document</h2>
            <p className="sub">The PDF investors download from your website</p>
            <div className="document">
                <span className="icon-tile"><FiFileText /></span>
                <div className="t">
                    <strong>{file ? file.name : pdfName}</strong>
                    <span>{file
                        ? 'Replaces the current PDF when you save'
                        : [row.pages > 0 && `${row.pages} pages`, fetched.year() > 2000 && `pulled from the ASX ${fetched.tz(TZ).format('D MMM, h:mm a')}`].filter(x => x).join(' · ')}</span>
                </div>
                {file
                    ? <button type="button" className="btn-outline" onClick={() => setFile(null)}>Keep current</button>
                    : <label className="btn-outline"><FiUpload /> Replace PDF<input type="file" accept=".pdf,application/pdf" onChange={e => pick(e.target.files?.[0])} /></label>}
            </div>
        </div>

        <div className="footer">
            <span className="note">Changes appear on your website within a minute.{row.badge !== 'Emailed' && ' The email draft is rebuilt from the new headline.'}</span>
            <button type="button" className="btn-delete-outline" onClick={() => setDeleting(true)} disabled={saving}><FiTrash2 /> Delete Announcement</button>
            <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving && <FiLoader className="spin" />} Save Changes</button>
        </div>

        {deleting && <DeleteAnnouncementModal row={row} onClose={() => setDeleting(false)} onDeleted={onDeleted} />}
    </div>;
};

export default AnnouncementEditForm;
